<template>
  <f7-card class="totals-breakdown elevation-4">
    <!-- Estimate disclaimer — only shown pre-SHIP. Members in DRAFT need to
         know the figures will move as the box fills, the commission rate is
         not yet locked, and the total isn't a bill. -->
    <f7-card-content
      v-if="total?.isEstimated"
      class="totals-disclaimer"
    >
      <f7-icon
        f7="info_circle_fill"
        size="18"
        color="orange"
      ></f7-icon>
      <span class="totals-disclaimer-text">{{ $t('box.totals.estimateDisclaimer') }}</span>
    </f7-card-content>

    <!-- Hero total — leads the card. Label varies by stage. -->
    <f7-card-content class="totals-hero">
      <div class="totals-hero-label">{{ heroLabel }}</div>
      <div class="totals-hero-value">{{ formatCurrency(total?.totalCents ?? 0) }}</div>
      <div v-if="lineCount > 0" class="totals-hero-meta">
        {{ $t('box.totals.lineCount', { count: lineCount }) }}
      </div>
    </f7-card-content>

    <!-- Breakdown of how the total was reached. -->
    <f7-card-content class="totals-rows">
      <div class="totals-row">
        <span class="totals-row-label">{{ $t('box.totals.subtotal') }}</span>
        <span class="totals-row-value">{{ formatCurrency(total?.subtotalCents ?? 0) }}</span>
      </div>

      <div class="totals-row">
        <span class="totals-row-label">
          {{ $t('box.totals.commission') }}
          <span
            v-if="commissionRatePct > 0"
            class="totals-row-rate"
          >({{ commissionRatePct }}%)</span>
        </span>
        <span class="totals-row-value">{{ formatCurrency(total?.commissionCents ?? 0) }}</span>
      </div>

      <!-- Adjustments rollup. The estimatedTotal contract only exposes a
           signed total (no per-line detail), so we render one row when
           non-zero and skip otherwise. The system 'rounding_down' entry is
           teased out into its own row below so subtotal + commission +
           adjustments + rounding = total is verifiable by the member. -->
      <div
        v-if="userAdjustmentsCents !== 0"
        class="totals-row totals-row-adjustment"
        :class="{ 'totals-row-negative': userAdjustmentsCents < 0 }"
      >
        <span class="totals-row-label">{{ $t('box.totals.adjustments') }}</span>
        <span class="totals-row-value">{{ formatSignedCurrency(userAdjustmentsCents) }}</span>
      </div>

      <!-- System rounding row. Always negative (rounds the pre-round total
           DOWN to the nearest pound — never up — so the member never pays
           more than the true sub-pound total). Treated as a discount visually
           but labeled distinctly so it doesn't read as an admin adjustment. -->
      <div
        v-if="roundingCents !== 0"
        class="totals-row totals-row-adjustment totals-row-rounding"
        :class="{ 'totals-row-negative': roundingCents < 0 }"
      >
        <span class="totals-row-label">
          {{ $t('box.totals.roundingDown') }}
        </span>
        <span class="totals-row-value">{{ formatSignedCurrency(roundingCents) }}</span>
      </div>

      <!-- PoolShare reward row. Renders as "potential" pre-settlement (gift
           icon) and "earned" once isFinal (seal icon). Hidden when the box
           has no poolshare-eligible items so we never show "+0.00". The
           value is informational — it does NOT enter the totalCents math
           (the wallet credit lands separately after delivery confirmation). -->
      <div
        v-if="potentialPoolShareCents > 0 && !total?.isFinal"
        class="totals-row totals-row-poolshare"
      >
        <span class="totals-row-label">
          <f7-icon
            f7="gift_fill"
            size="14"
            color="green"
            class="totals-row-poolshare-icon"
          ></f7-icon>
          {{ $t('box.totals.potentialPoolShare') }}
        </span>
        <span class="totals-row-value totals-row-poolshare-value">
          {{ formatSignedCurrency(potentialPoolShareCents) }}
        </span>
      </div>
      <div
        v-if="potentialPoolShareCents > 0 && total?.isFinal"
        class="totals-row totals-row-poolshare totals-row-poolshare-earned"
      >
        <span class="totals-row-label">
          <f7-icon
            f7="checkmark_seal_fill"
            size="14"
            color="green"
            class="totals-row-poolshare-icon"
          ></f7-icon>
          {{ $t('box.totals.earnedPoolShare') }}
        </span>
        <span class="totals-row-value totals-row-poolshare-value">
          {{ formatSignedCurrency(potentialPoolShareCents) }}
        </span>
      </div>
    </f7-card-content>

    <!-- Items: supporting detail beneath the financial summary. -->
    <template v-if="items?.length">
      <div class="totals-section-divider" />
      <f7-card-content class="totals-items-header">
        <span class="totals-items-heading">
          {{ $t('box.totals.items') }}
        </span>
        <span class="totals-items-count">{{ items.length }}</span>
      </f7-card-content>
      <f7-list
        no-hairlines
        no-hairlines-between
        media-list
        class="totals-items-list"
      >
        <f7-list-item
          v-for="{ item, issue } in decoratedItems"
          :key="item.id"
          :title="itemDisplayName(item)"
          :subtitle="item.isManual ? '' : item.channelName"
        >
          <template #media>
            <!-- Manual (off-catalog) lines have no catalog thumbnail — render
                 the branded monogram default instead of a broken <img>. -->
            <manual-item-avatar
              v-if="item.isManual"
              :label="itemDisplayName(item)"
              :size="48"
              :alt="$t('item.manual.avatarAlt')"
            ></manual-item-avatar>
            <img
              v-else
              :src="item.avatarUrl ?? item.productAvatarUrl"
              class="totals-item-thumb"
              :alt="itemDisplayName(item)"
            />
          </template>
          <template #after>
            <div class="totals-item-after">
              <!-- Unified member issue presentation (useItemIssuePresentation):
                   • ROUTABLE (needs-decision, or being-handled with a typed
                     issue) → these rows aren't links, so the shared chip renders
                     interactively (a real <button>, named with the item) and
                     opens the same /issues/:id/ card the order row + the
                     notification open. Being-handled lands there READ-ONLY: the
                     leader's message and the full timeline.
                   • being-handled with no issue behind it → the same calm pill,
                     non-interactive and chevron-less. Nothing to open.
                   • out-of-stock WITH issue history (`lastIssueId`, ASK 8) →
                     the same chip in its own muted hue, opening the resolved
                     issue read-only; with nothing behind it → calm terminal
                     pill, non-routing. -->
              <decision-chip
                v-if="issue.chipVariant"
                interactive
                :variant="issue.chipVariant"
                :issue-id="issue.issueId"
                :item-name="itemDisplayName(item)"
              ></decision-chip>
              <turn-pill
                v-else-if="issue.kind === ISSUE_PRESENTATION.BEING_HANDLED"
                variant="handled"
                :announce="false"
              ></turn-pill>
              <turn-pill
                v-else-if="issue.kind === ISSUE_PRESENTATION.OUT_OF_STOCK"
                variant="oos"
                :announce="false"
              ></turn-pill>
              <div class="totals-item-qty">
                <f7-icon f7="number" size="13" color="gray"></f7-icon>
                <span>{{ item.qty }}</span>
              </div>
              <div class="totals-item-amount">{{ formatCurrency(item.price * item.qty) }}</div>
            </div>
          </template>
        </f7-list-item>
      </f7-list>
    </template>

    <!-- CTA / metadata slot, injected by the page based on stage -->
    <slot name="footer" />
  </f7-card>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DecisionChip from '@/components/collection/DecisionChip.vue';
import TurnPill from '@/components/collection/TurnPill.vue';
import ManualItemAvatar from '@/components/orders/ManualItemAvatar.vue';
import { useCurrency } from '@/composables/useCurrency.js';
import {
  classifyItemIssue,
  issueChipVariant,
  ISSUE_PRESENTATION,
} from '@/composables/useItemIssuePresentation.js';
import { itemDisplayName } from '@/utils/orderBoxItemDisplay.js';

const props = defineProps({
  // The stage-aware estimatedTotal object from the API. Shape:
  //   { stage, subtotalCents, commissionCents, commissionRateBp,
  //     adjustmentsTotalCents, totalCents, lineCount, isEstimated, isFinal }
  total: {
    type: Object,
    required: true,
  },
  items: {
    type: Array,
    default: () => [],
  },
  // Per-line adjustment entries from the invoice payload. Used to split the
  // rolled-up adjustmentsTotalCents into a user-facing 'Adjustments' row and
  // a separate 'Round down' row. May be omitted for pre-SHIP draft payloads
  // where the backend has not yet persisted any entries.
  adjustmentsJson: {
    type: Array,
    default: () => [],
  },
  // Aggregated poolshare yield (cents) across all in-flight items in the box.
  // Informational only — not part of the totalCents math.
  potentialPoolShareCents: {
    type: Number,
    default: 0,
  },
});

const { t } = useI18n();
const { formatCurrency, formatSignedCurrency } = useCurrency();

// Classify each item's member issue presentation ONCE per render (pure, cheap),
// so the template branches on `issue.kind` without re-computing per v-if.
// `chipVariant` is folded in here for the same reason — the template must not
// call helpers per v-if branch. It is null when the item has no openable issue,
// which is exactly the chip's own v-if, so "show a chip" and "which court" can
// never disagree.
const decoratedItems = computed(() =>
  (props.items ?? []).map((item) => {
    const issue = classifyItemIssue(item);
    return { item, issue: { ...issue, chipVariant: issueChipVariant(issue) } };
  }),
);

const heroLabel = computed(() => {
  if (props.total?.isFinal) return t('box.totals.heroFinal');
  if (props.total?.isEstimated) return t('box.totals.heroEstimate');
  return t('box.totals.hero');
});

const commissionRatePct = computed(() => {
  const bp = props.total?.commissionRateBp ?? 0;
  return Number((bp / 100).toFixed(2));
});

const adjustmentsCents = computed(() => props.total?.adjustmentsTotalCents ?? 0);

// Sum of all rounding_down entries. Backend invariant: amount_cents < 0 and
// |amount_cents| < 100, so the displayed value is always a small discount.
// Tolerate snake_case (api) and camelCase (locally constructed) keys.
const roundingCents = computed(() => {
  const entries = props.adjustmentsJson;
  if (!Array.isArray(entries) || entries.length === 0) return 0;
  return entries
    .filter(e => e?.type === 'rounding_down')
    .reduce((sum, e) => sum + Number(e.amount_cents ?? e.amountCents ?? 0), 0);
});

// User-visible "Adjustments" row excludes the system rounding entry so the
// rows sum to total. If we showed adjustmentsTotalCents AND the rounding,
// the member would see double-counted numbers.
const userAdjustmentsCents = computed(() => adjustmentsCents.value - roundingCents.value);

const lineCount = computed(() => props.total?.lineCount ?? 0);

</script>

<style scoped>
.totals-breakdown {
    margin: 0;
    border-radius: var(--poolit-radius-lg);
    overflow: hidden;
}

.elevation-4 {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.totals-disclaimer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(255, 159, 10, 0.08);
    border-bottom: 1px solid rgba(255, 159, 10, 0.18);
}

.totals-disclaimer-text {
    font-size: 12px;
    line-height: 1.4;
    color: var(--poolit-text-secondary);
}

.totals-hero {
    padding: 18px 18px 14px;
    background: var(--poolit-bg-primary);
    border-bottom: 1px solid var(--poolit-border-color);
}

.totals-hero-label {
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 4px;
}

.totals-hero-value {
    font-size: 28px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-primary-600);
    line-height: 1.1;
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.totals-hero-meta {
    margin-top: 6px;
    font-size: 12px;
    color: var(--poolit-text-tertiary);
}

.totals-section-divider {
    height: 1px;
    background: var(--poolit-border-color);
    margin: 0;
}

.totals-items-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 12px 16px 4px;
}

.totals-items-heading {
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.totals-items-count {
    font-size: 13px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-secondary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.totals-items-list {
    margin: 0;
}

.totals-item-thumb {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    object-fit: cover;
}

.totals-item-after {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

/* DEAD (mark for cleanup pass): the bespoke "needs your decision" chip was
   replaced by the shared <decision-chip interactive> (DecisionChip.vue), which
   now owns this visual + the /issues/:id/ navigation. No markup references these
   rules any more — remove in the deferred cleanup pass. */
.totals-item-decision {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 30px;
    padding: 5px 11px;
    border: none;
    border-radius: var(--poolit-radius-full, 999px);
    background: var(--ci-await-bg, #fbefd4);
    color: var(--ci-await-fg, #b45309);
    font-family: inherit;
    font-size: var(--poolit-font-size-small, 0.75rem);
    font-weight: var(--poolit-font-weight-bold, 700);
    cursor: pointer;
}

.totals-item-decision__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.totals-item-decision:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--ci-await-ring, rgba(201, 130, 10, 0.5));
}

.totals-item-qty {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--poolit-text-tertiary);
}

.totals-item-amount {
    font-size: 14px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.totals-rows {
    padding: var(--poolit-spacing-md);
    display: flex;
    flex-direction: column;
    gap: var(--poolit-spacing-sm);
}

.totals-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--poolit-spacing-md);
}

.totals-row-label {
    font-size: 14px;
    color: var(--poolit-text-secondary);
}

.totals-row-rate {
    color: var(--poolit-text-tertiary);
    font-size: 12px;
    margin-inline-start: 4px;
}

.totals-row-value {
    font-size: 15px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.totals-row-adjustment .totals-row-label {
    font-size: 13px;
}

.totals-row-adjustment.totals-row-negative .totals-row-value {
    color: var(--poolit-success-600, #1b8a3b);
}

.totals-row-rounding .totals-row-label {
    color: var(--poolit-text-tertiary);
    font-style: italic;
}

.totals-row-poolshare .totals-row-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--poolit-text-secondary);
}

.totals-row-poolshare-value {
    color: var(--poolit-success-600, #1b8a3b);
}
</style>
